import React, { useEffect, useMemo } from 'react'

import { Page } from 'partials/Page'

import { Box } from 'widgets/Box'
import { FlatList } from 'widgets/FlatList'
import { TabView } from 'widgets/TabView'

import { useClientSubscriptions } from 'hooks/useClientSubscriptions'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useGroups } from 'hooks/useGroups'
import { usePendingMemberships } from 'hooks/usePendingMemberships'
import { useQueryClient } from 'hooks/useQueryClient'

import { Program } from 'types/program'

import { MembershipRow } from './MembershipRow'

type Props = {
  route: any
  navigation: any
}

export const MembershipsPage = ({ route, navigation }: Props) => {
  const queryClient = useQueryClient()
  const { data: currentUser } = useCurrentUser()
  const { data: subscriptions, isLoading: isLoadingSubscriptions } = useClientSubscriptions({
    userId: currentUser?.id,
  })

  const { data: pendingMemberships, isLoading: isLoadingPendingMemberships } =
    usePendingMemberships({
      userId: currentUser?.id,
    })

  const { data: pendingGroups } = useGroups(pendingMemberships?.map((pm) => pm.program.id) ?? [])

  const subscribedPrograms = useMemo(
    () => (subscriptions || []).map((sub) => sub.program),
    [subscriptions],
  )

  const legacySubscriptions = subscriptions
    ? // eslint-disable-next-line no-underscore-dangle
      subscriptions.filter((sub) => sub._original !== null)
    : []

  useEffect(() => {
    queryClient.invalidateQueries('subscriptions')
  }, [queryClient])

  return (
    <Page withNav navigation={navigation} withBack backgroundColor='white' title='Groups'>
      <TabView testID='MembershipsTab' tabs={['Pending', 'Active']}>
        {/* Pending Tab */}
        <Box flex={1}>
          {!isLoadingPendingMemberships &&
            pendingGroups &&
            pendingGroups.length > 0 &&
            currentUser && (
              <FlatList<Program>
                testID='PendingMembershipsFlatList'
                data={pendingGroups}
                loading={isLoadingPendingMemberships}
                renderItem={({ item: program }) => {
                  return (
                    <MembershipRow
                      mode='pending'
                      program={program}
                      navigation={navigation}
                      currentUser={currentUser}
                    />
                  )
                }}
                keyExtractor={(program) => `${program?.id}`}
              />
            )}
        </Box>

        {/* Active Tab */}
        <Box flex={1}>
          {!isLoadingSubscriptions &&
            subscribedPrograms &&
            subscribedPrograms.length > 0 &&
            currentUser && (
              <FlatList<Program>
                testID='ActiveMembershipsFlatList'
                data={subscribedPrograms}
                loading={isLoadingSubscriptions}
                renderItem={({ item: program }) => {
                  return (
                    <MembershipRow
                      mode='active'
                      program={program}
                      navigation={navigation}
                      currentUser={currentUser}
                      isLegacySubscription={legacySubscriptions.some(
                        (sub) => sub.program.id === program.id,
                      )}
                    />
                  )
                }}
                keyExtractor={(program) => `${program?.id}`}
              />
            )}
        </Box>
      </TabView>
    </Page>
  )
}
